import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";

import { IForgotPasswordFormValues } from "../../@types/ForgotPassword";
import { Button } from "../../components/Button";
import { TextField } from "../../components/TextField";
import {
  FooterLinksDesktop,
  FooterLinksMobile,
} from "../../layout/components/FooterLinks";
import { authService } from "../../services/authService";
import { preLoginLogo } from "../../styles/preLoginBranding";
import { mixpanelActions } from "../../utils/mixpanel";
import {
  Logo,
  Container,
  StyledTile,
  emailTextFieldStyles,
  sendButtonStyles,
  InfoMessage,
  Form,
  LoadingOverlay,
} from "./ForgotPasswordPage.styles";

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isSubmittingRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessMessage, setShowSucessMessage] = useState(false);
  const [apiFieldErrors, setApiFieldErrors] = useState({
    email: "",
  });

  const handleOnSubmitForm = async ({ email }: IForgotPasswordFormValues) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    mixpanelActions.track("User Action: ForgotPasswordEmailSubmit");

    setIsLoading(true);

    try {
      await authService.sendStaffForgotPasswordRequest({ email });
    } finally {
      setShowSucessMessage(true);
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const formValidationSchema = yup.object().shape({
    email: yup
      .string()
      .email(t("login_validation_emailInvalid"))
      .required(t("login_validation_emailRequired")),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    onSubmit: handleOnSubmitForm,
    validationSchema: formValidationSchema,
  });

  useEffect(() => {
    mixpanelActions.track("Visited Screen: ForgotPasswordEmail");
  }, []);

  const shouldShowErrorMessage = (field: "email") => {
    return formik.touched[field]
      ? apiFieldErrors[field] || formik.errors[field]
      : "";
  };

  const handleResetGlobalErrorFieldOnBlur = (field: "email") => {
    setApiFieldErrors((state: IForgotPasswordFormValues) => ({
      ...state,
      [field]: "",
    }));
  };

  return (
    <Container>
      <StyledTile>
        {isLoading && <LoadingOverlay />}
        <Form onSubmit={formik.handleSubmit}>
          <Logo src={preLoginLogo.src} alt={preLoginLogo.alt} />
          <InfoMessage>
            {!showSuccessMessage
              ? t("forgotPassword_instructions")
              : t("forgotPassword_checkEmail", { email: formik.values.email })}
          </InfoMessage>
          {!showSuccessMessage ? (
            <div>
              <TextField
                name="email"
                label={t("login_emailLabel")}
                value={formik.values.email}
                onChange={formik.handleChange}
                placeholder={t("login_emailPlaceholder")}
                className={emailTextFieldStyles()}
                errorMessage={shouldShowErrorMessage("email")}
                onBlur={() => handleResetGlobalErrorFieldOnBlur("email")}
              />
              <Button
                type="submit"
                label={t("forgotPassword_submit")}
                isLoading={isLoading}
                className={sendButtonStyles()}
                disabled={!formik.isValid}
              />
            </div>
          ) : (
            <div>
              <Button
                type="button"
                label={t("forgotPassword_backToLogin")}
                className={sendButtonStyles()}
                onClick={() => navigate("/login")}
              />
            </div>
          )}
        </Form>
        <FooterLinksMobile />
      </StyledTile>
      <FooterLinksDesktop />
    </Container>
  );
}
